/**
 * "Mijn adressen" — saved-address mini-CRUD on /account.
 *
 * Wraps the backend endpoints under /api/account/addresses to let
 * customers manage multiple delivery/billing addresses. Strictly
 * additive: storefront, checkout payment flow, Mollie, AA, Yuki,
 * Resend and bpost handling are untouched.
 *
 * UX rules:
 *   - street / houseNumber / bus are stored SEPARATELY; only the
 *     read-only card combines them into a display line.
 *   - BE/NL/DE require houseNumber (the backend enforces this; the
 *     UI mirrors the constraint for instant feedback).
 *   - Single-default invariant per type is enforced server-side; UI
 *     hides "Maak standaard …" actions that would conflict with the
 *     address type (e.g. SHIPPING-only can never be default billing).
 *   - Every mutation refetches the canonical list from the backend
 *     so the UI never silently drifts from the source of truth.
 */
import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Truck, Receipt, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { accountApi } from '../../api/client';

const inputClass = 'w-full px-3.5 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-shadow';

const TYPE_LABELS = {
  SHIPPING: 'Levering',
  BILLING: 'Facturatie',
  BOTH: 'Levering & facturatie',
};

const STRUCTURED_COUNTRIES = new Set(['BE', 'NL', 'DE']);

const EMPTY_FORM = {
  label: '', type: 'BOTH',
  firstName: '', lastName: '', company: '',
  street: '', houseNumber: '', bus: '',
  postalCode: '', city: '', countryCode: 'BE',
  phone: '',
};

function Field({ label, children, error, required }) {
  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1.5">
        {label}{required && <span className="text-destructive"> *</span>}
      </label>
      {children}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}

function displayLine(a) {
  const main = [a.street, a.houseNumber].filter(Boolean).join(' ');
  return a.bus ? `${main} bus ${a.bus}` : main;
}

function localValidate(form) {
  const errs = {};
  if (!form.street?.trim()) errs.street = 'Verplicht';
  if (!form.postalCode?.trim()) errs.postalCode = 'Verplicht';
  if (!form.city?.trim()) errs.city = 'Verplicht';
  if (!form.countryCode?.trim()) errs.countryCode = 'Verplicht';
  if (STRUCTURED_COUNTRIES.has((form.countryCode || '').toUpperCase())
      && !form.houseNumber?.trim()) {
    errs.houseNumber = `Verplicht voor ${form.countryCode.toUpperCase()}`;
  }
  return errs;
}

export default function AddressesSection() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // id | "__new__" | null
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrs, setFormErrs] = useState({});
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const { data } = await accountApi.listAddresses();
      setAddresses(data?.addresses || []);
    } catch {
      toast.error('Kon adressen niet laden.');
    }
  };

  useEffect(() => {
    let alive = true;
    accountApi.listAddresses()
      .then(({ data }) => { if (alive) setAddresses(data?.addresses || []); })
      .catch(() => { if (alive) toast.error('Kon adressen niet laden.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const startNew = () => {
    setEditingId('__new__');
    setForm(EMPTY_FORM);
    setFormErrs({});
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({
      label: a.label || '', type: a.type || 'BOTH',
      firstName: a.firstName || '', lastName: a.lastName || '',
      company: a.company || '',
      street: a.street || '', houseNumber: a.houseNumber || '',
      bus: a.bus || '',
      postalCode: a.postalCode || '', city: a.city || '',
      countryCode: a.countryCode || 'BE',
      phone: a.phone || '',
    });
    setFormErrs({});
  };

  const cancel = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormErrs({});
  };

  const submit = async () => {
    const errs = localValidate(form);
    setFormErrs(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const payload = { ...form, countryCode: (form.countryCode || 'BE').toUpperCase() };
      if (editingId === '__new__') {
        await accountApi.createAddress(payload);
        toast.success('Adres toegevoegd.');
      } else {
        await accountApi.updateAddress(editingId, payload);
        toast.success('Adres bijgewerkt.');
      }
      await reload();
      cancel();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Opslaan mislukt.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Adres "${a.label || displayLine(a)}" verwijderen?`)) return;
    setBusy(true);
    try {
      await accountApi.deleteAddress(a.id);
      toast.success('Adres verwijderd.');
      await reload();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Verwijderen mislukt.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const setDefault = async (a, kind) => {
    setBusy(true);
    try {
      await accountApi.setAddressDefault(a.id,
        kind === 'shipping' ? { shipping: true } : { billing: true });
      toast.success(kind === 'shipping' ? 'Standaard levering ingesteld.'
                                          : 'Standaard facturatie ingesteld.');
      await reload();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Instellen mislukt.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mb-8" data-testid="addresses-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl font-light text-foreground">Mijn adressen</h2>
        {editingId === null && (
          <button data-testid="add-address-btn" onClick={startNew}
            className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:underline">
            <Plus className="w-3.5 h-3.5" /> Adres toevoegen
          </button>
        )}
      </div>

      {loading && (
        <div className="bg-card border border-border rounded-2xl p-6 text-sm text-muted-foreground">
          Adressen laden…
        </div>
      )}

      {!loading && addresses.length === 0 && editingId === null && (
        <div className="bg-card border border-border rounded-2xl p-6 text-sm text-muted-foreground">
          Nog geen bewaarde adressen. Voeg er één toe om sneller af te rekenen.
        </div>
      )}

      {!loading && addresses.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          {addresses.map(a => (
            <article key={a.id} data-testid={`address-card-${a.id}`}
              className="bg-card border border-border rounded-2xl p-4 shadow-sm space-y-2">
              <header className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-foreground truncate">
                    {a.label || `${a.firstName || ''} ${a.lastName || ''}`.trim() || 'Adres'}
                  </p>
                  <p className="text-xs text-muted-foreground">{TYPE_LABELS[a.type] || a.type}</p>
                </div>
                <div className="flex flex-col gap-1 shrink-0 text-right">
                  {a.isDefaultShipping && (
                    <span data-testid={`badge-default-shipping-${a.id}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold text-green-700 bg-green-100/70 px-1.5 py-0.5 rounded">
                      <Truck className="w-3 h-3" /> Standaard levering
                    </span>
                  )}
                  {a.isDefaultBilling && (
                    <span data-testid={`badge-default-billing-${a.id}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold text-blue-700 bg-blue-100/70 px-1.5 py-0.5 rounded">
                      <Receipt className="w-3 h-3" /> Standaard facturatie
                    </span>
                  )}
                </div>
              </header>
              <div className="text-sm text-foreground">
                <p>{displayLine(a) || '—'}</p>
                <p className="text-muted-foreground text-xs">{a.postalCode} {a.city} · {a.countryCode}</p>
                {a.phone && <p className="text-muted-foreground text-xs">{a.phone}</p>}
              </div>
              <div className="flex flex-wrap items-center gap-3 pt-1">
                <button data-testid={`edit-address-${a.id}`} onClick={() => startEdit(a)} disabled={busy}
                  className="inline-flex items-center gap-1 text-xs text-primary font-medium hover:underline disabled:opacity-50">
                  <Pencil className="w-3 h-3" /> Bewerken
                </button>
                <button data-testid={`delete-address-${a.id}`} onClick={() => remove(a)} disabled={busy}
                  className="inline-flex items-center gap-1 text-xs text-destructive font-medium hover:underline disabled:opacity-50">
                  <Trash2 className="w-3 h-3" /> Verwijderen
                </button>
                {!a.isDefaultShipping && (a.type === 'SHIPPING' || a.type === 'BOTH') && (
                  <button data-testid={`set-default-shipping-${a.id}`}
                    onClick={() => setDefault(a, 'shipping')} disabled={busy}
                    className="text-xs text-muted-foreground hover:text-foreground hover:underline disabled:opacity-50">
                    Maak standaard levering
                  </button>
                )}
                {!a.isDefaultBilling && (a.type === 'BILLING' || a.type === 'BOTH') && (
                  <button data-testid={`set-default-billing-${a.id}`}
                    onClick={() => setDefault(a, 'billing')} disabled={busy}
                    className="text-xs text-muted-foreground hover:text-foreground hover:underline disabled:opacity-50">
                    Maak standaard facturatie
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {editingId !== null && (
        <div className="bg-card border border-border rounded-2xl p-5 shadow-sm space-y-4"
             data-testid="address-form">
          <h3 className="font-serif text-lg font-medium text-foreground">
            {editingId === '__new__' ? 'Nieuw adres' : 'Adres bewerken'}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Label">
              <input data-testid="address-label" value={form.label}
                onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                className={inputClass} placeholder='bv. "Thuis", "Praktijk"' />
            </Field>
            <Field label="Type">
              <select data-testid="address-type" value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                className={inputClass}>
                <option value="BOTH">Levering & facturatie</option>
                <option value="SHIPPING">Alleen levering</option>
                <option value="BILLING">Alleen facturatie</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Voornaam">
              <input data-testid="address-first-name" value={form.firstName}
                onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                className={inputClass} />
            </Field>
            <Field label="Familienaam">
              <input data-testid="address-last-name" value={form.lastName}
                onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                className={inputClass} />
            </Field>
          </div>
          <Field label="Bedrijf">
            <input data-testid="address-company" value={form.company}
              onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
              className={inputClass} placeholder="optioneel" />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <Field label="Straat" required error={formErrs.street}>
                <input data-testid="address-street" value={form.street}
                  onChange={e => setForm(f => ({ ...f, street: e.target.value }))}
                  className={inputClass} />
              </Field>
            </div>
            <Field label="Huisnummer" required error={formErrs.houseNumber}>
              <input data-testid="address-house-number" value={form.houseNumber}
                onChange={e => setForm(f => ({ ...f, houseNumber: e.target.value }))}
                className={inputClass} />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Bus / appt.">
              <input data-testid="address-bus" value={form.bus}
                onChange={e => setForm(f => ({ ...f, bus: e.target.value }))}
                className={inputClass} placeholder="optioneel" />
            </Field>
            <Field label="Postcode" required error={formErrs.postalCode}>
              <input data-testid="address-postal" value={form.postalCode}
                onChange={e => setForm(f => ({ ...f, postalCode: e.target.value }))}
                className={inputClass} />
            </Field>
            <Field label="Stad" required error={formErrs.city}>
              <input data-testid="address-city" value={form.city}
                onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                className={inputClass} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Land" required error={formErrs.countryCode}>
              <input data-testid="address-country" value={form.countryCode}
                onChange={e => setForm(f => ({ ...f, countryCode: e.target.value.toUpperCase() }))}
                className={inputClass} maxLength={2} />
            </Field>
            <Field label="Telefoon">
              <input data-testid="address-phone" type="tel" value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className={inputClass} placeholder="+32 ..." />
            </Field>
          </div>
          <p className="text-xs text-muted-foreground">
            Straat, huisnummer en bus worden apart bewaard voor
            correcte verzending.
          </p>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button data-testid="address-cancel" type="button" onClick={cancel} disabled={busy}
              className="px-4 py-2 rounded-full border border-border text-sm text-muted-foreground hover:bg-muted disabled:opacity-50">
              Annuleren
            </button>
            <button data-testid="address-save" type="button" onClick={submit} disabled={busy}
              className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {editingId === '__new__' ? 'Toevoegen' : 'Opslaan'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
